import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, CalendarClock, ChevronRight, Plus, ShieldPlus, Sparkles, Swords, Target, Trophy, Users2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/atoms/Card";
import { Button } from "@/components/atoms/Button";
import { EmptyState } from "@/components/organisms/EmptyState";
import { SkeletonCard } from "@/components/atoms/Skeleton";
import { ErrorState } from "@/components/organisms/ErrorState";
import { StadiumIllustration, TrophyIllustration } from "@/components/atoms/illustrations";
import { ROUTES } from "@/constants/routes";
import { useLeagues } from "@/modules/leagues/hooks/useLeagues";
import { useUpcomingMatchesAcrossLeagues } from "@/modules/leagues/hooks/useMatches";
import { usePendingBonusQuestionsAcrossLeagues } from "@/modules/leagues/hooks/usePendingBonusQuestions";
import { usePendingDuelsAcrossLeagues } from "@/modules/leagues/hooks/usePendingDuels";
import { LeagueCard } from "@/modules/leagues/components/LeagueCard";
import { MatchCard } from "@/modules/leagues/components/MatchCard";
import heroStadium from "@/assets/hero-stadium.jpg";

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Bom dia";
  if (hour < 18) return "Boa tarde";
  return "Boa noite";
}

const FEATURES = [
  { icon: ShieldPlus, label: "Crie ligas" },
  { icon: Trophy, label: "Convide amigos" },
  { icon: Target, label: "Faça palpites" },
  { icon: Award, label: "Dispute rankings" },
];

export function HomePage() {
  const { profile, firebaseUser } = useAuth();
  const navigate = useNavigate();
  const firstName = profile?.nome?.split(" ")[0] ?? "";
  const { data: leagues, isLoading: loadingLeagues, isError: leaguesError, refetch: refetchLeagues } = useLeagues();
  // Origem REAL dos indicadores "Palpites pendentes" / "Ao vivo agora" —
  // os campos denormalizados na Liga (pendingPredictionsCount/hasLiveMatch)
  // são gravados só na criação e NUNCA atualizados depois (bug real
  // encontrado na auditoria: ficavam para sempre em 0/false). Calculamos
  // ao vivo a partir das partidas de verdade, já buscadas pra aba Jogos.
  const { data: upcomingMatches, isLoading: loadingMatches } = useUpcomingMatchesAcrossLeagues(leagues);
  const { data: pendingBonusQuestions } = usePendingBonusQuestionsAcrossLeagues(leagues);
  const { data: pendingDuels } = usePendingDuelsAcrossLeagues(leagues, firebaseUser?.uid);

  const pendingTotal = (upcomingMatches ?? []).filter((m) => m.status === "scheduled" && !m.userPrediction && !m.predictionsLocked).length;
  const liveCount = (upcomingMatches ?? []).filter((m) => m.status === "live").length;
  const nextMatches = (upcomingMatches ?? []).filter((m) => m.status !== "finished").slice(0, 4);

  return (
    <div className="space-y-6 pb-4">
      {/* Hero de boas-vindas — vivo mesmo sem nenhum grupo criado ainda */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="stadium-glow relative overflow-hidden rounded-2xl p-5 text-white shadow-glow-primary"
        style={{
          backgroundImage: `url(${heroStadium})`,
          backgroundSize: "cover",
          backgroundPosition: "center 65%",
        }}
      >
        {/* Duas versões da sobreposição — a foto é a mesma nos dois temas,
            mas o tratamento muda: no escuro, escurece à esquerda pra texto
            branco; no claro, clareia à esquerda pra texto escuro (igual à
            referência) e mantém uma faixa azulada no topo pra saudação em
            branco continuar legível nos dois casos. */}
        <div
          className="absolute inset-0 hidden dark:block"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(90deg, rgba(7,23,53,0.94) 0%, rgba(9,35,79,0.88) 32%, rgba(13,71,161,0.55) 60%, rgba(13,71,161,0.18) 85%)",
          }}
        />
        <div
          className="absolute inset-0 dark:hidden"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, rgba(9,35,79,0.62) 0%, rgba(9,35,79,0) 34%), linear-gradient(100deg, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.95) 48%, rgba(230,242,253,0.75) 70%, rgba(235,244,253,0.3) 90%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-hero-radial dark:block hidden" aria-hidden="true" />
        <div className="relative space-y-4">
          <div>
            <p className="flex items-center gap-1.5 text-sm font-medium text-white/90">
              {getGreeting()}{firstName ? `, ${firstName}` : ""} <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            </p>
            <h1 className="font-display text-xl font-bold leading-snug text-foreground dark:text-white">
              Bem-vindo ao
              <br />
              <span className="text-primary dark:text-accent-400">Palpiteiro</span>{" "}
              <span className="text-foreground dark:text-white">Certo</span>
            </h1>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
            {FEATURES.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 text-xs font-medium text-foreground dark:text-white/90"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-white text-primary shadow-soft dark:border-white/25 dark:bg-white/10 dark:text-white dark:shadow-none">
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                {label}
              </span>
            ))}
          </div>

          <Button
            variant="accent"
            size="sm"
            onClick={() => navigate(ROUTES.createLeague)}
            className="shadow-none"
          >
            <Plus className="h-4 w-4" /> Criar minha primeira liga
          </Button>
        </div>
      </motion.div>

      {pendingBonusQuestions && pendingBonusQuestions.length > 0 && (        <div className="space-y-2">
          {pendingBonusQuestions.map((p) => (
            <button
              key={p.leagueId}
              onClick={() => navigate(ROUTES.bonusQuestionsAnswer(p.leagueId))}
              className="flex w-full items-center gap-3 rounded-2xl border border-accent-500/40 bg-accent-50 px-4 py-3 text-left shadow-glow-accent transition-colors hover:bg-accent-100 dark:bg-accent-500/10 dark:hover:bg-accent-500/20"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-500/20 text-accent-700 dark:text-accent-300">
                <Award className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-accent-700 dark:text-accent-300">Perguntas Bônus pendentes</p>
                <p className="truncate text-xs text-muted-foreground">
                  {p.leagueName} · {p.countMissing} pergunta{p.countMissing > 1 ? "s" : ""} sem resposta
                </p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-accent-700 dark:text-accent-300" />
            </button>
          ))}
        </div>
      )}

      {pendingDuels && pendingDuels.length > 0 && (
        <div className="space-y-2">
          {pendingDuels.map((d) => (
            <button
              key={d.leagueId}
              onClick={() => navigate(ROUTES.duels(d.leagueId))}
              className="flex w-full items-center gap-3 rounded-2xl border border-primary/40 bg-primary-50 px-4 py-3 text-left shadow-glow-primary transition-colors hover:bg-primary-100 dark:bg-primary-900/30 dark:hover:bg-primary-900/50"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <Swords className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-primary">Duelos 1x1</p>
                <p className="truncate text-xs text-muted-foreground">
                  {d.leagueName} ·{" "}
                  {d.pendingForMe > 0 ? `${d.pendingForMe} desafio${d.pendingForMe > 1 ? "s" : ""} esperando sua resposta` : ""}
                  {d.pendingForMe > 0 && d.pendingSentByMe > 0 ? " · " : ""}
                  {d.pendingSentByMe > 0 ? `${d.pendingSentByMe} enviado${d.pendingSentByMe > 1 ? "s" : ""}, aguardando` : ""}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-primary" />
            </button>
          ))}
        </div>
      )}

      {/* Stats rápidas — clicáveis, abrem a área correspondente (item 28/29) */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-0 bg-gradient-to-br from-primary-50 to-white dark:from-primary-900/30 dark:to-card">
          <button onClick={() => navigate(ROUTES.groups)} className="w-full text-left">
            <CardContent className="flex flex-col items-center gap-1.5 p-4 text-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                <Users2 className="h-4 w-4" />
              </div>
              <span className="font-display text-lg font-bold leading-none">{leagues?.length ?? 0}</span>
              <span className="text-[11px] text-muted-foreground">Minhas ligas</span>
              <span className="mt-0.5 h-1 w-8 rounded-full bg-primary" aria-hidden="true" />
            </CardContent>
          </button>
        </Card>
        <Card className="border-0 bg-gradient-to-br from-primary-50 to-white dark:from-primary-900/30 dark:to-card">
          <button onClick={() => navigate(ROUTES.matches)} className="w-full text-left">
            <CardContent className="flex flex-col items-center gap-1.5 p-4 text-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                <CalendarClock className="h-4 w-4" />
              </div>
              <span className="font-display text-lg font-bold leading-none">
                {loadingMatches ? "…" : pendingTotal}
              </span>
              <span className="text-[11px] text-muted-foreground">Palpites pendentes</span>
              <span className="mt-0.5 h-1 w-8 rounded-full bg-primary" aria-hidden="true" />
            </CardContent>
          </button>
        </Card>
        <Card className="border-0 bg-gradient-to-br from-accent-50 to-white dark:from-primary-900/30 dark:to-card">
          <button onClick={() => navigate(ROUTES.matches)} className="w-full text-left">
            <CardContent className="flex flex-col items-center gap-1.5 p-4 text-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-gold text-primary-900">
                <Trophy className="h-4 w-4" />
              </div>
              <span className="font-display text-lg font-bold leading-none">
                {loadingMatches ? "…" : liveCount}
              </span>
              <span className="text-[11px] text-muted-foreground">Ao vivo agora</span>
              <span className="mt-0.5 h-1 w-8 rounded-full bg-accent-400" aria-hidden="true" />
            </CardContent>
          </button>
        </Card>
      </div>

      {/* Minhas ligas */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-1.5 font-display text-sm font-semibold">
            <Trophy className="h-4 w-4 text-accent-600" aria-hidden="true" /> Minhas ligas
          </h2>
          {leagues && leagues.length > 0 && (
            <button
              onClick={() => navigate(ROUTES.groups)}
              className="flex items-center text-xs font-medium text-primary hover:underline"
            >
              Ver todas <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {loadingLeagues && (
          <div className="space-y-3">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {leaguesError && (
          <ErrorState
            title="Não foi possível carregar suas ligas"
            description="Verifique sua conexão e tente novamente."
            onRetry={() => refetchLeagues()}
          />
        )}

        {leagues && leagues.length === 0 && (
          <EmptyState
            illustration={TrophyIllustration}
            title="Você ainda não participa de nenhuma liga"
            description="Crie um bolão para a família, os amigos ou o trabalho, ou entre com um código de convite."
            actionLabel="Criar liga"
            onAction={() => navigate(ROUTES.createLeague)}
            secondaryActionLabel="Tenho um código"
            onSecondaryAction={() => navigate(ROUTES.joinLeague)}
          />
        )}

        {leagues && leagues.length > 0 && (
          <div className="space-y-3">
            {leagues.map((league) => (
              <LeagueCard key={league.id} league={league} onClick={() => navigate(ROUTES.leaguePredictions(league.id))} />
            ))}
          </div>
        )}
      </section>

      {/* Próximos jogos globais (todas as ligas) — dados reais, item 30 */}
      {leagues && leagues.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-1.5 font-display text-sm font-semibold">
              <CalendarClock className="h-4 w-4 text-primary" aria-hidden="true" /> Próximos jogos
            </h2>
            <button
              onClick={() => navigate(ROUTES.matches)}
              className="flex items-center text-xs font-medium text-primary hover:underline"
            >
              Ver todos <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {loadingMatches && (
            <div className="space-y-3">
              <SkeletonCard />
            </div>
          )}

          {!loadingMatches && (!upcomingMatches || upcomingMatches.length === 0) && (
            <EmptyState
              illustration={StadiumIllustration}
              title="Nenhum jogo por aqui ainda"
              description="Assim que a rodada atual das suas ligas tiver jogos, eles aparecem aqui."
            />
          )}

          {!loadingMatches && nextMatches.length > 0 && (
            <div className="space-y-3">
              {nextMatches.map((match) => (
                <MatchCard
                  key={`${match.leagueId}-${match.id}`}
                  match={match}
                  leagueId={match.leagueId}
                  scoringRules={match.scoringRules}
                  totalParticipants={match.totalParticipants}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Teaser de gamificação */}
      <Card className="overflow-hidden border-0 bg-gradient-card-blue text-white shadow-glow-primary">
        <CardContent className="flex items-center gap-4 p-5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <p className="font-display text-sm font-semibold">Ranking, medalhas e XP chegando</p>
            <p className="text-xs text-white/70">Dispute posições e desbloqueie conquistas nas próximas etapas.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
